import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { safeStorage } from './safeStorage.ts';

export type ThemePreference = 'system' | 'light' | 'dark';
export type MotionPreference = 'system' | 'reduced';
export type DensityPreference = 'comfortable' | 'compact';
export type AnimationTogglePreference = 'on' | 'off';
export type AnimationSpeedPreference = 'slow' | 'normal' | 'fast';

export interface PreferencesState {
  theme: ThemePreference;
  motion: MotionPreference;
  density: DensityPreference;
  animation: AnimationTogglePreference;
  animationSpeed: AnimationSpeedPreference;
  mouseAnimation: AnimationTogglePreference;
  setTheme: (theme: ThemePreference) => void;
  setMotion: (motion: MotionPreference) => void;
  setDensity: (density: DensityPreference) => void;
  setAnimation: (animation: AnimationTogglePreference) => void;
  setAnimationSpeed: (speed: AnimationSpeedPreference) => void;
  setMouseAnimation: (mouseAnimation: AnimationTogglePreference) => void;
}

export const usePreferences = create<PreferencesState>()(
  persist(
    (set) => ({
      theme: 'system',
      motion: 'system',
      density: 'comfortable',
      animation: 'on',
      animationSpeed: 'normal',
      mouseAnimation: 'on',
      setTheme: (theme) => set({ theme }),
      setMotion: (motion) => set({ motion }),
      setDensity: (density) => set({ density }),
      setAnimation: (animation) => set({ animation }),
      setAnimationSpeed: (animationSpeed) => set({ animationSpeed }),
      setMouseAnimation: (mouseAnimation) => set({ mouseAnimation }),
    }),
    {
      name: 'traceira-preferences',
      version: 2,
      storage: createJSONStorage(() => safeStorage),
      partialize: ({ theme, motion, density, animation, animationSpeed, mouseAnimation }) => ({
        theme,
        motion,
        density,
        animation,
        animationSpeed,
        mouseAnimation,
      }),
    },
  ),
);

/** Mirrors preferences onto <html> so tokens.css can switch roles without JS styling. */
export function applyPreferences({
  theme,
  motion,
  density,
  animation,
  animationSpeed,
  mouseAnimation,
}: Partial<
  Pick<
    PreferencesState,
    'theme' | 'motion' | 'density' | 'animation' | 'animationSpeed' | 'mouseAnimation'
  >
>) {
  const root = document.documentElement;
  if (theme === 'system') delete root.dataset.theme;
  else if (theme) root.dataset.theme = theme;
  if (motion === 'reduced') root.dataset.motion = 'reduced';
  else if (motion) delete root.dataset.motion;
  if (density === 'compact') root.dataset.density = 'compact';
  else if (density) delete root.dataset.density;

  if (animation === 'off') root.dataset.animation = 'off';
  else if (animation) delete root.dataset.animation;

  if (animationSpeed && animationSpeed !== 'normal') root.dataset.animationSpeed = animationSpeed;
  else delete root.dataset.animationSpeed;

  if (mouseAnimation === 'off') root.dataset.mouseAnimation = 'off';
  else if (mouseAnimation) delete root.dataset.mouseAnimation;
}
